import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CloudDetailsView from "../components/CloudDetailsView";
import CloudConnectDialog from "../components/CloudConnectDialog";
import { listClouds } from "../api/clouds";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../routes";

const PROVIDERS = [
  {
    id: "m365",
    name: "Microsoft 365",
    logo: "/m365.jpg",
    description: "License usage, user assignment, and subscription visibility.",
  },
  {
    id: "azure",
    name: "Microsoft Azure",
    logo: "/azure.jpg",
    description: "Subscription health, resource overview, and cloud governance.",
  },
  {
    id: "aws",
    name: "Amazon Web Services",
    logo: "/aws.png",
    description: "EC2, S3, RDS, Lambda, and cost monitoring in one view.",
  },
];

function providerName(id) {
  const found = PROVIDERS.find(p => p.id === id);
  return found ? found.name : String(id || "Unknown").toUpperCase();
}

function statusMeta(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return { label: "Connected", color: "success", icon: <CloudDoneIcon fontSize="small" /> };
  if (s === "pending") return { label: "Pending", color: "warning", icon: <HourglassBottomIcon fontSize="small" /> };
  return { label: "Needs Attention", color: "error", icon: <ErrorOutlineIcon fontSize="small" /> };
}

export default function CloudConnectionsPage() {
  const navigate = useNavigate();
  const [clouds, setClouds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCloudId, setSelectedCloudId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");

  const loadClouds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listClouds();
      const list = Array.isArray(res) ? res : res?.data || [];
      setClouds(list);
      setSelectedCloudId(prev => {
        if (!list.length) return "";
        const stillExists = list.some(c => c.id === prev);
        return stillExists ? prev : list[0].id;
      });
    } catch (err) {
      console.error("cloud list failed", err);
      setClouds([]);
      setSelectedCloudId("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadClouds();
  }, [loadClouds]);

  const stats = useMemo(() => {
    const total = clouds.length;
    const connected = clouds.filter(c => String(c.status || "").toLowerCase() === "active").length;
    const pending = clouds.filter(c => String(c.status || "").toLowerCase() === "pending").length;
    const attention = Math.max(0, total - connected - pending);
    return { total, connected, pending, attention };
  }, [clouds]);

  const providerState = useMemo(() => {
    const map = {};
    PROVIDERS.forEach(p => {
      map[p.id] = clouds.find(c => String(c.provider || "").toLowerCase() === p.id) || null;
    });
    return map;
  }, [clouds]);

  const selectedCloud = clouds.find(c => c.id === selectedCloudId);

  const providerRoute = (provider) => {
    const p = String(provider || "").toLowerCase();
    if (p === "aws") return ROUTES.CLOUD_AWS;
    if (p === "azure") return ROUTES.CLOUD_AZURE;
    if (p === "m365") return ROUTES.CLOUD_M365;
    return ROUTES.CLOUD;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ minHeight: "40vh", display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #E2E8F0", mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0F172A">
              Cloud Connections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connect and monitor cloud platforms with real-time connection health.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Cloud
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
          <Chip label={`Total: ${stats.total}`} variant="outlined" />
          <Chip label={`Connected: ${stats.connected}`} color="success" variant="outlined" />
          <Chip label={`Pending: ${stats.pending}`} color="warning" variant="outlined" />
          <Chip label={`Needs Attention: ${stats.attention}`} color="error" variant="outlined" />
        </Stack>
      </Paper>

      {clouds.length === 0 && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {PROVIDERS.map(provider => {
            const linked = providerState[provider.id];
            const linkedStatus = linked ? statusMeta(linked.status) : null;
            return (
              <Grid item xs={12} md={4} key={provider.id}>
                <Card sx={{ borderRadius: 2, border: "1px solid #E2E8F0", boxShadow: "none", height: "100%" }}>
                  <CardHeader
                    avatar={
                      <Box
                        component="img"
                        src={provider.logo}
                        alt={provider.name}
                        sx={{ width: 34, height: 34, borderRadius: 1, objectFit: "cover" }}
                      />
                    }
                    title={<Typography fontWeight={700}>{provider.name}</Typography>}
                    subheader={
                      linked ? (
                        <Chip
                          size="small"
                          icon={linkedStatus.icon}
                          label={linkedStatus.label}
                          color={linkedStatus.color}
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Chip size="small" label="Not Connected" variant="outlined" sx={{ mt: 0.5 }} />
                      )
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {provider.description}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        setSelectedProvider(provider.id);
                        setDialogOpen(true);
                      }}
                    >
                      Connect {provider.name}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {clouds.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #E2E8F0", mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Connected Tenants
              </Typography>
              <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                <Chip label={`Total: ${stats.total}`} variant="outlined" size="small" />
                <Chip label={`Connected: ${stats.connected}`} color="success" variant="outlined" size="small" />
                <Chip label={`Pending: ${stats.pending}`} color="warning" variant="outlined" size="small" />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 320 }}>
                <Select value={selectedCloudId} onChange={e => setSelectedCloudId(e.target.value)}>
                  {clouds.map(cloud => {
                    const meta = statusMeta(cloud.status);
                    return (
                      <MenuItem key={cloud.id} value={cloud.id}>
                        {providerName(cloud.provider)} - {meta.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {selectedCloud && (
                <Button
                  variant="text"
                  onClick={() => navigate(providerRoute(selectedCloud.provider))}
                >
                  Open Monitor
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {selectedCloud && (
        <CloudDetailsView cloudId={selectedCloudId} onRefresh={loadClouds} onDelete={loadClouds} />
      )}

      <CloudConnectDialog
        open={dialogOpen}
        defaultProvider={selectedProvider}
        onClose={() => {
          setDialogOpen(false);
          setSelectedProvider("");
        }}
        onSuccess={async () => {
          setDialogOpen(false);
          setSelectedProvider("");
          await loadClouds();
        }}
      />
    </Container>
  );
}
